<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import axios from '../axios.js'
import PlayerGames from './PlayerGames.vue'
import SeasonRow from './seasons/SeasonRow.vue'
import SeasonSelectionPopup from './seasons/SeasonSelectionPopup.vue'
import SeasonDetailsModal from './seasons/SeasonDetailsModal.vue'
import LeagueDetailsModal from './seasons/LeagueDetailsModal.vue'
import MinutesSettingsTable from './MinutesSettingsTable.vue'
import TemplateConfigurator from './TemplateConfigurator.vue'

const props = defineProps({
  content: {
    type: Object,
    required: true
  }
})

const player = computed(() => props.content || {})
const activeTab = ref('Profile')

const tabs = [
  'Profile',
  'Assigned Labels',
  'Aliases',
  'Splits',
  'Minutes Settings',
  'Games', 
  'Totals',
  'Teammates',
  'Opponents',
  'Embed'
]

const setActiveTab = (tab) => {
  activeTab.value = tab
  
  // Fetch splits data when Splits tab is selected
  if (tab === 'Splits') {
    fetchSplitsData()
  }
  
  // Fetch teams and leagues data when Minutes Settings tab is selected
  if (tab === 'Minutes Settings') {
    fetchTeamsAndLeaguesData()
  }
  
  // Fetch assigned labels data when Assigned Labels tab is selected
  if (tab === 'Assigned Labels') {
    fetchAssignedLabelsData()
  }
  
  // Fetch aliases data when Aliases tab is selected
  if (tab === 'Aliases') {
    fetchAliasesData()
  }
}

// Function to open season details modal
const openSeasonDetailsModal = (season, playerId, context = 'splits') => {
  selectedSeasonForModal.value = season
  selectedPlayerIdForModal.value = playerId
  modalContext.value = context
  showSeasonDetailsModal.value = true
}

// Function to close season details modal
const closeSeasonDetailsModal = () => {
  showSeasonDetailsModal.value = false
  selectedTemplateData.value = null
  modalContext.value = 'splits' // Reset to default
}

// Function to open league details modal
const openLeagueDetailsModal = (league, team, timeFrame = null) => {
  selectedLeagueForModal.value = league
  selectedTeamForModal.value = team
  selectedTimeFrameForModal.value = timeFrame
  showLeagueDetailsModal.value = true
}

// Function to handle opening season details modal from minutes settings table
const handleOpenSeasonDetails = (data) => {
  const playerId = data.playerId || player.value.id || player.value.player_id
  // Store the template data for the modal to use
  selectedTemplateData.value = {
    leagues: data.leagues || [],
    seasons: data.seasons || [],
    additionalFilter: data.additionalFilter || 'allGames'
  }
  console.log('Storing template data:', selectedTemplateData.value)
  console.log('Original data from minutes table:', data)
  openSeasonDetailsModal(data.templateLabel, playerId, 'minutes')
}

// Function to close league details modal
const closeLeagueDetailsModal = () => {
  showLeagueDetailsModal.value = false
  selectedLeagueForModal.value = null
  selectedTeamForModal.value = null
  selectedTimeFrameForModal.value = null
}

// Function to open template configurator
const openTemplateConfigurator = (templateLabel) => {
  selectedTemplateLabel.value = templateLabel
  showTemplateConfigurator.value = true
}

// Function to close template configurator
const closeTemplateConfigurator = () => {
  showTemplateConfigurator.value = false
  selectedTemplateLabel.value = ''
}

// Function to handle template saved
const handleTemplateSaved = async () => {
  // Refresh splits data
  await fetchSplitsData()
}

// Function to save minutes settings
const saveMinutesSettings = async () => {
  const playerId = player.value.id || player.value.player_id
  
  if (!playerId) {
    console.error('Player ID not found')
    return
  }
  
  try {
    const response = await axios.post(`/api/v2/projections-players/minutes-settings/${playerId}`, {
      minutesData: minutesData.value
    })
    
    console.log('Minutes settings saved successfully:', response.data)
    // You can add a success notification here if needed
  } catch (error) {
    console.error('Error saving minutes settings:', error)
    // You can add an error notification here if needed
  }
}

// Function to fetch splits data from API
const fetchSplitsData = async () => {
  const playerId = player.value.id || player.value.player_id
  
  if (!playerId) {
    splitsError.value = 'Player ID not found'
    return
  }
  
  isLoadingSplits.value = true
  splitsError.value = null
  
  try {
    const response = await axios.get(`/api/v2/projections-players/splits/${playerId}`)
    splitsData.value = response.data
  } catch (error) {
    console.error('Error fetching splits data:', error)
    splitsError.value = error.response?.data?.message || 'Failed to fetch splits data'
  } finally {
    isLoadingSplits.value = false
  }
}

// Function to fetch teams and leagues data from API
const fetchTeamsAndLeaguesData = async () => {
  const playerId = player.value.id || player.value.player_id
  
  if (!playerId) {
    teamsLeaguesError.value = 'Player ID not found'
    return
  }
  
  isLoadingTeamsLeagues.value = true
  teamsLeaguesError.value = null
  
  try {
    const response = await axios.get(`/api/v2/projections-players/teams-with-league/by-player/${playerId}`)
    teamsLeaguesData.value = response.data
    minutesSettingsData.value = response.data // Store the same data for minutes settings
    console.log('Teams and leagues data fetched:', response.data)
    
    // Initialize minutes data structure for each league
    if (response.data && response.data.length > 0) {
      response.data.forEach((item, itemIndex) => {
        if (item.team && item.team.leagues && item.team.leagues.length > 0) {
          item.team.leagues.forEach((league, leagueIndex) => {
            const key = `${item.team.team_name}_${league.league?.league_name}`
            if (!minutesData.value[key]) {
              minutesData.value[key] = {
                lastSeason: 0,
                twoSeasonsAgo: 0,
                threeSeasonsAgo: 0,
                lastFiveGames: 0,
                withoutLast5Games: 0
              }
            }
          })
        }
      })
    }
  } catch (error) {
    console.error('Error fetching teams and leagues data:', error)
    teamsLeaguesError.value = error.response?.data?.message || 'Failed to fetch teams and leagues data'
  } finally {
    isLoadingTeamsLeagues.value = false
  }
}

// Function to fetch assigned labels data from API
const fetchAssignedLabelsData = async () => {
  const playerId = player.value.id || player.value.player_id
  
  if (!playerId) {
    assignedLabelsError.value = 'Player ID not found'
    return
  }
  
  isLoadingAssignedLabels.value = true
  assignedLabelsError.value = null
  
  try {
    const response = await axios.get(`/api/v2/projections-players/assigned-labels/${playerId}`)
    assignedLabelsData.value = response.data
    console.log('Assigned labels data fetched:', response.data)
  } catch (error) {
    console.error('Error fetching assigned labels data:', error)
    assignedLabelsError.value = error.response?.data?.message || 'Failed to fetch assigned labels data'
  } finally {
    isLoadingAssignedLabels.value = false
  }
}

// Function to fetch aliases data from API
const fetchAliasesData = async () => {
  const playerId = player.value.id || player.value.player_id
  
  if (!playerId) {
    aliasesError.value = 'Player ID not found'
    return
  }
  
  isLoadingAliases.value = true
  aliasesError.value = null
  
  try {
    const response = await axios.get(`/api/players/aliases/${playerId}`)
    aliasesData.value = response.data
    console.log('Aliases data fetched:', response.data)
  } catch (error) {
    console.error('Error fetching aliases data:', error)
    aliasesError.value = error.response?.data?.message || 'Failed to fetch aliases data'
  } finally {
    isLoadingAliases.value = false
  }
}

// Function to fetch available labels
const fetchAvailableLabels = async () => {
  isLoadingLabels.value = true
  try {
    const response = await axios.get('/api/v2/projections-players/all-player-label-names')
    availableLabels.value = response.data
    console.log('Available labels fetched:', response.data)
  } catch (error) {
    console.error('Error fetching available labels:', error)
    alert('Failed to fetch available labels: ' + (error.response?.data?.message || error.message))
  } finally {
    isLoadingLabels.value = false
  }
}

// Function to show label popup
const showLabelSelection = async (assignmentId) => {
  selectedAssignmentId.value = assignmentId
  showLabelPopup.value = true
  
  // Always fetch available labels when opening popup
  await fetchAvailableLabels()
}

// Function to close label popup
const closeLabelPopup = () => {
  showLabelPopup.value = false
  selectedAssignmentId.value = null
  labelSearchQuery.value = ''
}

// Computed property for filtered labels
const filteredLabels = computed(() => {
  if (!labelSearchQuery.value) return availableLabels.value
  
  return availableLabels.value.filter(label => {
    const name = (label.name || label.label_name || '').toLowerCase()
    const description = (label.description || label.label_description || '').toLowerCase()
    const query = labelSearchQuery.value.toLowerCase()
    
    return name.includes(query) || description.includes(query)
  })
})

// Function to assign a label to a player assignment
const assignLabel = async (labelId) => {
  try {
    // Find the current assignment data
    const currentAssignment = assignedLabelsData.value.find(assignment => assignment.id === selectedAssignmentId.value)
    
    if (!currentAssignment) {
      throw new Error('Assignment not found')
    }
    
    // Prepare the data to send
    const requestData = {
      player_label_id: parseInt(labelId),
      player_id: currentAssignment.player_id,
      team_data: {
        team_id: currentAssignment.active_team_id,
        team_name: currentAssignment.team?.team_name || 'Unknown Team'
      },
      assignment_id: selectedAssignmentId.value
    }
    
    console.log('Sending assignment data:', requestData)
    
    const response = await axios.post(`/api/v2/projections-players/assigned-labels/save`, requestData)
    
    console.log('Label assigned successfully:', response.data)
    // Refresh the data
    await fetchAssignedLabelsData()
    // Close popup
    closeLabelPopup()
  } catch (error) {
    console.error('Error assigning label:', error)
    alert('Failed to assign label: ' + (error.response?.data?.message || error.message))
  }
}

// Function to remove a label from a player assignment
const removeAssignedLabel = async (assignmentId) => {
  if (!confirm('Are you sure you want to remove this label?')) return
  
  try {
    const response = await axios.put(`/api/v2/projections-players/assigned-labels/${assignmentId}`, {
      player_label_id: null
    })
    
    console.log('Label removed successfully:', response.data)
    // Refresh the data
    await fetchAssignedLabelsData()
  } catch (error) {
    console.error('Error removing label:', error)
    alert('Failed to remove label: ' + (error.response?.data?.message || error.message))
  }
}

// Season templates data
const availableSeasons = ref([
  'Last Season',
  '2 Seasons ago',
  '3 Seasons ago',
  'League Avg',
  'Last 5 games',
  'Without Last 5'
])

const selectedSeasons = ref(['Last Season', '2 Seasons ago', 'League Avg'])

// Season selection popup state
const showSeasonSelectionPopup = ref(false)

// Season selection handling
const handleSeasonSelection = (seasons) => {
  selectedSeasons.value = seasons
}

const openSeasonSelection = () => {
  showSeasonSelectionPopup.value = true
}

const closeSeasonSelection = () => {
  showSeasonSelectionPopup.value = false
}

// Season data updates
const seasonDataUpdates = ref({})

const handleSeasonDataUpdate = ({ season, data }) => {
  seasonDataUpdates.value[season] = data
}

const handleSeasonSelected = (season) => {
  // Handle individual season selection if needed
  console.log('Season selected:', season)
}

const handleSeasonFiltersApplied = (filterData) => {
  console.log('Filters applied for season:', filterData.season, filterData.filters)
  // You can handle the filter data here if needed
}

// Minutes filter functions
const openMinutesFilter = () => {
  showMinutesFilter.value = true
}

const closeMinutesFilter = () => {
  showMinutesFilter.value = false
}

const handleMinutesFilterSelection = (filter) => {
  selectedMinutesFilter.value = filter
  showMinutesFilter.value = false
}

// Editable data arrays
const activeTeams = ref([])
const playerLabels = ref([])

// Watch for player changes and update reactive data
watch(() => player.value, (newPlayer, oldPlayer) => {
  if (newPlayer) {
    activeTeams.value = newPlayer.activeTeams || [
      newPlayer.team || 'Kalev/Cramo',
      newPlayer.nationalTeam || 'Estonian National Basketball Team'
    ].filter(team => team)
    
    playerLabels.value = [
      newPlayer.label1 || 'Multi Year Returning Player',
      newPlayer.label2 || 'Returning Player'
    ].filter(label => label)
    
    // Reset tab and clear cached data when player changes
    if (oldPlayer && (oldPlayer.id !== newPlayer.id || oldPlayer.player_id !== newPlayer.player_id)) {
      activeTab.value = 'Profile'
      splitsData.value = null
      aliasesData.value = null
      assignedLabelsData.value = null
      teamsLeaguesData.value = null
      minutesSettingsData.value = null
      gamesData.value = []
    }
  }
}, { immediate: true })

const isLoadingSplits = ref(false)
const splitsData = ref(null)
const splitsError = ref(null)

// Teams and Leagues data
const isLoadingTeamsLeagues = ref(false)
const teamsLeaguesData = ref(null)
const teamsLeaguesError = ref(null)

// Minutes Settings data
const minutesSettingsData = ref(null)

// Assigned Labels data
const isLoadingAssignedLabels = ref(false)
const assignedLabelsData = ref(null)
const assignedLabelsError = ref(null)

// Aliases data
const isLoadingAliases = ref(false)
const aliasesData = ref(null)
const aliasesError = ref(null)

// Season Summary data (computed from games data)
const gamesData = ref([])

// Function to receive games data from PlayerGames component
const updateGamesData = (games) => {
  gamesData.value = games
}

// Computed property to create season summary from games data
const seasonSummaryData = computed(() => {
  if (!gamesData.value || gamesData.value.length === 0) {
    return []
  }

  // Group games by season and team
  const seasonMap = new Map()
  
  gamesData.value.forEach(game => {
    const season = game.season || 'Unknown Season'
    const teamName = game.team_name || game.team || 'Unknown Team'
    const leagueName = game.league_name || game.league || 'Unknown League'
    
    if (!seasonMap.has(season)) {
      seasonMap.set(season, {
        season: season,
        total_usage: 0,
        teams: new Map()
      })
    }
    
    const seasonData = seasonMap.get(season)
    seasonData.total_usage++
    
    if (!seasonData.teams.has(teamName)) {
      seasonData.teams.set(teamName, {
        team_id: game.team_id || Math.random(),
        team_name: teamName,
        league_name: leagueName,
        usage_count: 0
      })
    }
    
    seasonData.teams.get(teamName).usage_count++
  })
  
  // Convert to flat array format for Excel-like table
  const flatRows = []
  
  Array.from(seasonMap.values()).forEach(season => {
    Array.from(season.teams.values()).forEach(team => {
      flatRows.push({
        season: season.season,
        team_name: team.team_name,
        league_name: team.league_name,
        games_count: team.usage_count,
        total_season_games: season.total_usage,
        percentage: Math.round((team.usage_count / season.total_usage) * 100)
      })
    })
  })
  
  // Sort by season (newest first) and then by games count (highest first)
  return flatRows.sort((a, b) => {
    // Extract year from season string (e.g., "2023-24" -> 2023)
    const getYear = (seasonStr) => {
      const match = seasonStr.match(/(\d{4})/)
      return match ? parseInt(match[1]) : 0
    }
    
    const yearA = getYear(a.season)
    const yearB = getYear(b.season)
    
    // First sort by year descending (newest first)
    if (yearB !== yearA) {
      return yearB - yearA
    }
    
    // Then sort by games count descending (highest first)
    return b.games_count - a.games_count
  })
})

// Label selection popup state
const showLabelPopup = ref(false)
const selectedAssignmentId = ref(null)
const availableLabels = ref([])
const isLoadingLabels = ref(false)
const labelSearchQuery = ref('')

// Minutes Settings data - will be populated dynamically based on teams/leagues
const minutesData = ref({})

// Minutes filter state
const showMinutesFilter = ref(false)
const selectedMinutesFilter = ref('All Minutes')

// Minutes filter options
const minutesFilterOptions = ref([
  'All Minutes',
  'Last 5 games',
  'Rest of Curr season', 
  'Last Season',
  '2 Seasons ago',
  '3 Seasons ago'
])

// Season details modal state
const showSeasonDetailsModal = ref(false)
const selectedSeasonForModal = ref('')
const selectedPlayerIdForModal = ref(null)
const selectedTemplateData = ref(null)
const modalContext = ref('splits') // 'splits' or 'minutes'

// League details modal state
const showLeagueDetailsModal = ref(false)
const selectedLeagueForModal = ref(null)
const selectedTeamForModal = ref(null)
const selectedTimeFrameForModal = ref(null)

// Template configurator state
const showTemplateConfigurator = ref(false)
const selectedTemplateLabel = ref('')

// Editing states
const editingTeam = ref(null)
const editingLabel = ref(null)
const newTeam = ref('')
const newLabel = ref('')

// Minutes Settings
const minutesSettings = computed(() => ({
  minMinutes: player.value.min_minutes || 0,
  maxMinutes: player.value.max_minutes || 48,
  targetMinutes: player.value.target_minutes || 24,
  minutesPerGame: player.value.minutes_per_game || 0,
  restDays: player.value.rest_days || 0,
  backToBack: player.value.back_to_back || false,
  overtime: player.value.overtime || false
}))

const editingMinutes = ref(false)

// Team functions
const addTeam = () => {
  newTeam.value = ' '
}

const saveTeam = () => {
  if (newTeam.value.trim()) {
    activeTeams.value.push(newTeam.value.trim())
    newTeam.value = ''
  }
}

const removeTeam = (index) => {
  activeTeams.value.splice(index, 1)
}

const startEditTeam = (index) => {
  editingTeam.value = index
}

const saveEditTeam = (index) => {
  editingTeam.value = null
}

const cancelEditTeam = () => {
  editingTeam.value = null
}

// Label functions
const addLabel = () => {
  newLabel.value = ' '
}

const saveLabel = () => {
  if (newLabel.value.trim()) {
    playerLabels.value.push(newLabel.value.trim())
    newLabel.value = ''
  }
}

const removeLabel = (index) => {
  playerLabels.value.splice(index, 1)
}

const startEditLabel = (index) => {
  editingLabel.value = index
}

const saveEditLabel = (index) => {
  editingLabel.value = null
}

const cancelEditLabel = () => {
  editingLabel.value = null
}

// Click outside handler for popup
const handleClickOutside = (event) => {
  if (showLabelPopup.value) {
    const popup = event.target.closest('.label-popup')
    if (!popup) {
      closeLabelPopup()
    }
  }
}

// Prevent popup from closing when clicking inside
const handlePopupClick = (event) => {
  event.stopPropagation()
}

// Add and remove event listeners
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})


</script>

<template>
  <div class="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl shadow-lg overflow-hidden border border-gray-200">
    <!-- Header with player image and info -->
    <div class="flex flex-col items-center pt-10 pb-6 relative bg-gradient-to-b from-gray-50 to-blue-50">
      <div class="w-36 h-36 rounded-full border-4 border-gray-300 shadow-md overflow-hidden bg-white">
        <img :src="player.avatar || '/default_avatar.png'" alt="Player photo" class="w-full h-full object-cover" />
      </div>
      <div class="mt-4 text-center">
        <div class="text-3xl font-bold text-gray-900 mb-1">{{ player.player_name || 'Player Name' }}</div>
        <div class="text-gray-600 text-lg font-semibold uppercase tracking-wide">{{ player.team || 'Team Name' }}</div>
        <div class="flex items-center justify-center gap-2 mt-1">
          <div class="text-gray-500 text-base">Player Statistics</div>
          <a v-if="player.player_link" 
             :href="player.player_link" 
             target="_blank" 
             class="text-blue-500 hover:text-blue-700 text-sm"
             title="View on ProBallers"
          >
            <i class="fas fa-external-link-alt"></i>
          </a>
        </div>
      </div>
      <!-- Info grid -->
      <div class="mt-6 bg-white rounded-lg px-8 py-4 flex gap-12 justify-center shadow-sm border border-gray-200">
        <div class="text-center">
          <div class="text-xs text-gray-500 font-medium">DATE OF BIRTH</div>
          <div class="text-gray-900 font-semibold">{{ player.birthdate || player.bd || 'N/A' }}</div>
        </div>
        <div class="text-center">
          <div class="text-xs text-gray-500 font-medium">HEIGHT</div>
          <div class="text-gray-900 font-semibold">{{ player.height_cm ? player.height_cm + ' cm' : 'N/A' }}</div>
        </div>
        <div v-if="player.nationality" class="text-center">
          <div class="text-xs text-gray-500 font-medium">NATIONALITY</div>
          <div class="text-gray-900 font-semibold">{{ player.nationality }}</div>
        </div>
        <div v-if="player.age" class="text-center">
          <div class="text-xs text-gray-500 font-medium">AGE</div>
          <div class="text-gray-900 font-semibold">{{ player.age }}</div>
        </div>
      </div>
      <!-- Stats row -->
      <div class="mt-6 w-full max-w-2xl mx-auto bg-white rounded-lg flex justify-between px-8 py-3 text-center font-semibold text-lg shadow-sm border border-gray-200">
        <div class="flex-1">
          <div class="text-xs font-medium text-gray-500">PTS</div>
          <div class="text-blue-600">{{ player.pts ?? 0 }}</div>
        </div>
        <div class="flex-1">
          <div class="text-xs font-medium text-gray-500">REB</div>
          <div class="text-blue-600">{{ player.reb ?? 0 }}</div>
        </div>
        <div class="flex-1">
          <div class="text-xs font-medium text-gray-500">AST</div>
          <div class="text-blue-600">{{ player.ast ?? 0 }}</div>
        </div>
        <div class="flex-1">
          <div class="text-xs font-medium text-gray-500">STL</div>
          <div class="text-blue-600">{{ player.stl ?? 0 }}</div>
        </div>
        <div class="flex-1">
          <div class="text-xs font-medium text-gray-500">BLK</div>
          <div class="text-blue-600">{{ player.blk ?? 0 }}</div>
        </div>
      </div>
    </div>
    <!-- Tabs -->
    <div class="bg-white/95 backdrop-blur-sm px-8 pt-2 pb-0 flex space-x-8 border-b border-gray-200">
      <button 
        v-for="tab in tabs" 
        :key="tab"
        @click="setActiveTab(tab)"
        :class="[
          'py-3 px-2 font-semibold transition-all duration-200 cursor-pointer',
          activeTab === tab 
            ? 'text-blue-600 border-b-2 border-blue-500' 
            : 'text-gray-500 hover:text-blue-600 hover:border-b-2 hover:border-blue-300'
        ]"
      >
        {{ tab }}
      </button>
    </div>
    <!-- Bio and shortcuts -->
    <div class="bg-white/90 backdrop-blur-sm px-8 py-8">
      <!-- Profile Tab Content -->
      <div v-if="activeTab === 'Profile'" class="space-y-6">
        <!-- Player Information Table -->
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div class="grid grid-cols-2 divide-x divide-gray-200">
            <!-- Active Teams Section -->
            <div>
              <div class="bg-gray-50 p-3 text-sm font-medium text-gray-700 border-b border-gray-200 flex justify-between items-center">
                Active Teams
                <button @click="addTeam" class="text-blue-600 hover:text-blue-800 text-xs font-medium">+ Add</button>
              </div>
              
              <!-- Existing Teams -->
              <div v-for="(team, index) in activeTeams" :key="index" class="border-b border-gray-200">
                <div class="p-3 text-sm text-gray-900 flex justify-between items-center group">
                  <div v-if="editingTeam !== index" class="flex-1">{{ team }}</div>
                  <input 
                    v-else 
                    v-model="activeTeams[index]" 
                    @blur="saveEditTeam(index)"
                    @keyup.enter="saveEditTeam(index)"
                    @keyup.esc="cancelEditTeam"
                    class="flex-1 border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    ref="teamInput"
                  />
                  <div class="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button @click="startEditTeam(index)" class="text-blue-600 hover:text-blue-800 text-xs">Edit</button>
                    <button @click="removeTeam(index)" class="text-red-600 hover:text-red-800 text-xs">Remove</button>
                  </div>
                </div>
              </div>
              
              <!-- Add New Team -->
              <div v-if="newTeam" class="p-3 border-b border-gray-200">
                <div class="flex space-x-2">
                  <input 
                    v-model="newTeam" 
                    @keyup.enter="saveTeam"
                    @keyup.esc="newTeam = ''"
                    placeholder="Enter team name..."
                    class="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button @click="saveTeam" class="text-blue-600 hover:text-blue-800 text-xs font-medium">Save</button>
                  <button @click="newTeam = ''" class="text-gray-500 hover:text-gray-700 text-xs">Cancel</button>
                </div>
              </div>
            </div>
            
            <!-- Player Label Section -->
            <div>
              <div class="bg-gray-50 p-3 text-sm font-medium text-gray-700 border-b border-gray-200 flex justify-between items-center">
                Player Label
                <button @click="addLabel" class="text-blue-600 hover:text-blue-800 text-xs font-medium">+ Add</button>
              </div>
              
              <!-- Existing Labels -->
              <div v-for="(label, index) in playerLabels" :key="index" class="border-b border-gray-200">
                <div class="p-3 text-sm text-gray-900 flex justify-between items-center group">
                  <div v-if="editingLabel !== index" class="flex-1">{{ label }}</div>
                  <input 
                    v-else 
                    v-model="playerLabels[index]" 
                    @blur="saveEditLabel(index)"
                    @keyup.enter="saveEditLabel(index)"
                    @keyup.esc="cancelEditLabel"
                    class="flex-1 border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    ref="labelInput"
                  />
                  <div class="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button @click="startEditLabel(index)" class="text-blue-600 hover:text-blue-800 text-xs">Edit</button>
                    <button @click="removeLabel(index)" class="text-red-600 hover:text-red-800 text-xs">Remove</button>
                  </div>
                </div>
              </div>
              
              <!-- Add New Label -->
              <div v-if="newLabel" class="p-3 border-b border-gray-200">
                <div class="flex space-x-2">
                  <input 
                    v-model="newLabel" 
                    @keyup.enter="saveLabel"
                    @keyup.esc="newLabel = ''"
                    placeholder="Enter label..."
                    class="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button @click="saveLabel" class="text-blue-600 hover:text-blue-800 text-xs font-medium">Save</button>
                  <button @click="newLabel = ''" class="text-gray-500 hover:text-gray-700 text-xs">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>


  
      </div>

      <!-- Games Tab Content -->
      <div v-else-if="activeTab === 'Games'" class="space-y-6">
        <!-- Season Summary Section -->
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div class="bg-gray-50 p-4 border-b border-gray-200">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Season Summary</h3>
              <p class="text-sm text-gray-600 mt-1">Player usage by season and teams (based on games data)</p>
            </div>
          </div>
          
          <!-- Content Container -->
          <div class="overflow-x-auto overflow-y-auto flex-1">
            <!-- Data Display -->
            <div v-if="seasonSummaryData && seasonSummaryData.length > 0" class="p-6">
              <!-- Excel-like Table -->
              <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table class="w-full">
                  <thead class="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Season</th>
                      <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Team</th>
                      <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">League</th>
                      <th class="px-4 py-3 text-center text-sm font-semibold text-gray-700">Games</th>
                      <th class="px-4 py-3 text-center text-sm font-semibold text-gray-700">Total Season</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in seasonSummaryData" :key="`${row.season}-${row.team_name}-${index}`" class="border-b border-gray-200 hover:bg-gray-50">
                      <td class="px-4 py-3 text-sm text-gray-900 font-medium">
                        <div class="flex items-center">
                          <i class="fas fa-calendar-alt text-blue-500 mr-2"></i>
                          {{ row.season }}
                        </div>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-900">
                        <div class="flex items-center">
                          <i class="fas fa-shield-alt text-blue-500 mr-2"></i>
                          {{ row.team_name }}
                        </div>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-700">
                        <div class="inline-flex items-center bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                          <i class="fas fa-trophy text-green-600 mr-1"></i>
                          {{ row.league_name }}
                        </div>
                      </td>
                      <td class="px-4 py-3 text-center text-sm text-gray-700 font-medium">
                        {{ row.games_count }}
                      </td>
                      <td class="px-4 py-3 text-center text-sm text-gray-600">
                        {{ row.total_season_games }}
                      </td>
                      
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <!-- Raw JSON data (collapsible for debugging) -->
              <details class="mt-6">
                <summary class="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800">
                  <i class="fas fa-code mr-2"></i>
                  View Computed Data
                </summary>
                <div class="mt-2 bg-gray-50 p-4 rounded-lg">
                  <pre class="text-xs text-gray-700 bg-white p-3 rounded border overflow-auto max-h-64">{{ JSON.stringify(seasonSummaryData, null, 2) }}</pre>
                </div>
              </details>
            </div>
            
            <!-- No Data State -->
            <div v-else class="flex items-center justify-center py-12">
              <div class="text-center text-gray-500">
                <i class="fas fa-chart-line text-2xl mb-2"></i>
                <p class="text-sm">No season summary data available. Games data will be loaded below.</p>
              </div>
            </div>
          </div>
        </div>

        <PlayerGames :player-id="player.id || player.player_id" @games-loaded="updateGamesData" />
      </div>

      <!-- Splits Tab Content -->
      <div v-else-if="activeTab === 'Splits'" class="space-y-6">
        <!-- Main Content -->
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div class="bg-gray-50 p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Player Splits Data</h3>
              <p class="text-sm text-gray-600 mt-1">Click on season names to filter data</p>
            </div>
            <div class="flex gap-2">
              <button 
                @click="openSeasonSelection"
                class="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center gap-2"
              >
                <i class="fas fa-filter"></i>
                Filter Seasons
              </button>
              <button 
                @click="fetchSplitsData"
                :disabled="isLoadingSplits"
                class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <i v-if="isLoadingSplits" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-sync-alt"></i>
                {{ isLoadingSplits ? 'Loading...' : 'Refresh' }}
              </button>
            </div>
          </div>
          
          <!-- Content Container -->
          <div class="overflow-x-auto overflow-y-auto flex-1">
            <!-- Loading State -->
            <div v-if="isLoadingSplits" class="flex items-center justify-center py-12">
              <div class="text-center text-slate-500">
                <i class="fas fa-spinner fa-spin text-2xl mb-2"></i>
                <p class="text-sm">Loading splits data...</p>
              </div>
            </div>
            
            <!-- Error State -->
            <div v-else-if="splitsError" class="flex items-center justify-center py-12">
              <div class="text-center text-red-500">
                <i class="fas fa-exclamation-triangle text-2xl mb-2"></i>
                <p class="text-sm">{{ splitsError }}</p>
                <button 
                  @click="fetchSplitsData"
                  class="mt-2 px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                >
                  Retry
                </button>
              </div>
            </div>
            
            <!-- Data Display -->
            <div v-else-if="splitsData" class="p-6">
              <!-- Table for splits data -->
              <table class="w-full border-collapse">
                <thead>
                  <tr class="bg-slate-50 border-b border-slate-200">
                    <th class="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-r border-slate-200">Season</th>
                    <th class="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-r border-slate-200">Per game minutes</th>
                    <th class="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-r border-slate-200">Game count</th>
                    <th class="px-3 py-2 text-center text-xs font-semibold text-slate-700 border-r border-slate-200">Points Per30</th>
                    <th class="px-3 py-2 text-center text-xs font-semibold text-slate-700 border-r border-slate-200">Total Rebounds Per30</th>
                    <th class="px-3 py-2 text-center text-xs font-semibold text-slate-700 border-r border-slate-200">Assists Per30</th>
                    <th class="px-3 py-2 text-center text-xs font-semibold text-slate-700 border-r border-slate-200">Blocks Per30</th>
                    <th class="px-3 py-2 text-center text-xs font-semibold text-slate-700 border-r border-slate-200">Three Point Field Goal Per30</th>
                    <th class="px-3 py-2 text-center text-xs font-semibold text-slate-700 border-r border-slate-200">Steals Per30</th>
                    <th class="px-3 py-2 text-center text-xs font-semibold text-slate-700">Turnovers Per30</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="splitsData.length === 0">
                    <tr>
                      <td colspan="8" class="px-3 py-8 text-center text-gray-500">
                        <div class="flex flex-col items-center gap-2">
                          <i class="fas fa-database text-2xl text-gray-300"></i>
                          <p>No splits data available.</p>
                        </div>
                      </td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr v-for="(item, index) in splitsData" :key="item.id" class="border-b border-slate-200 hover:bg-slate-50">
                      <td class="px-3 py-3 text-sm text-gray-900 border-r border-slate-200 font-medium">
                        <button 
                          @click="openTemplateConfigurator(item.template_label)"
                          class="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                        >
                          {{ item.template_label }}
                        </button>
                      </td>
                      <td class="px-3 py-3 text-sm text-gray-700 text-center border-r border-slate-200">
                        {{ item.per_game_minutes || '0.00' }}
                      </td>
                      <td class="px-3 py-3 text-sm text-gray-700 text-center border-r border-slate-200">
                        {{ item.game_count || '0.00' }}
                      </td>
                      <td class="px-3 py-3 text-sm text-gray-700 text-center border-r border-slate-200">
                        {{ item.points || '0.00' }}
                      </td>
                      <td class="px-3 py-3 text-sm text-gray-700 text-center border-r border-slate-200">
                        {{ item.total_rebounds || '0.00' }}
                      </td>
                      <td class="px-3 py-3 text-sm text-gray-700 text-center border-r border-slate-200">
                        {{ item.assists || '0.00' }}
                      </td>
                      <td class="px-3 py-3 text-sm text-gray-700 text-center border-r border-slate-200">
                        {{ item.blocks || '0.00' }}
                      </td>
                      <td class="px-3 py-3 text-sm text-gray-700 text-center border-r border-slate-200">
                        {{ item.three_points_field_goal || '0.00' }}
                      </td>
                      <td class="px-3 py-3 text-sm text-gray-700 text-center border-r border-slate-200">
                        {{ item.steals || '0.00' }}
                      </td>
                      <td class="px-3 py-3 text-sm text-gray-700 text-center">
                        {{ item.turnovers || '0.00' }}
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
              
              <!-- Raw JSON data (collapsible for debugging) -->
              <details class="mt-6">
                <summary class="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800">
                  <i class="fas fa-code mr-2"></i>
                  View Raw API Response
                </summary>
                <div class="mt-2 bg-gray-50 p-4 rounded-lg">
                  <pre class="text-xs text-gray-700 bg-white p-3 rounded border overflow-auto max-h-64">{{ JSON.stringify(splitsData, null, 2) }}</pre>
                </div>
              </details>
            </div>
            
            <!-- No Data State -->
            <div v-else class="flex items-center justify-center py-12">
              <div class="text-center text-gray-500">
                <i class="fas fa-chart-bar text-2xl mb-2"></i>
                <p class="text-sm">No splits data available. Click "Refresh" to fetch data.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Minutes Settings Tab Content -->
      <div v-else-if="activeTab === 'Minutes Settings'" class="space-y-6">
        <div class="bg-white rounded-lg ">

          
          <div class="">
            <!-- Minutes Settings Table -->
            <div class="mt-6">
              <MinutesSettingsTable 
                :data="minutesSettingsData"
                :isLoading="isLoadingTeamsLeagues"
                :error="teamsLeaguesError"
                @retry="fetchTeamsAndLeaguesData"
                @open-season-details="handleOpenSeasonDetails"
              />
            </div>
            

          </div>
        </div>
      </div>

      <!-- Assigned Labels Tab Content -->
      <div v-else-if="activeTab === 'Assigned Labels'" class="space-y-6">
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div class="bg-gray-50 p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Player Label Assignments</h3>
              <p class="text-sm text-gray-600 mt-1">View player label assignments and team associations.</p>
            </div>
            <div class="flex gap-2">
              <button 
                @click="openSeasonSelection"
                class="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center gap-2"
              >
                <i class="fas fa-filter"></i>
                Filter Seasons
              </button>
              <button 
                @click="fetchAssignedLabelsData"
                :disabled="isLoadingAssignedLabels"
                class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <i v-if="isLoadingAssignedLabels" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-sync-alt"></i>
                {{ isLoadingAssignedLabels ? 'Loading...' : 'Refresh' }}
              </button>
            </div>
          </div>
          
          <!-- Content Container -->
          <div class="overflow-x-auto overflow-y-auto flex-1">
            <!-- Loading State -->
            <div v-if="isLoadingAssignedLabels" class="flex items-center justify-center py-12">
              <div class="text-center text-slate-500">
                <i class="fas fa-spinner fa-spin text-2xl mb-2"></i>
                <p class="text-sm">Loading assigned labels data...</p>
              </div>
            </div>
            
            <!-- Error State -->
            <div v-else-if="assignedLabelsError" class="flex items-center justify-center py-12">
              <div class="text-center text-red-500">
                <i class="fas fa-exclamation-triangle text-2xl mb-2"></i>
                <p class="text-sm">{{ assignedLabelsError }}</p>
                <button 
                  @click="fetchAssignedLabelsData"
                  class="mt-2 px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                >
                  Retry
                </button>
              </div>
            </div>
            
            <!-- Data Display -->
            <div v-else-if="assignedLabelsData" class="p-6">
              <h4 class="text-md font-semibold text-gray-800 mb-3">
                <i class="fas fa-tags text-blue-500 mr-2"></i>
                Team Label Assignments
              </h4>
              <div v-if="assignedLabelsData.length === 0" class="text-center text-gray-500 py-12">
                <i class="fas fa-info-circle text-2xl mb-2"></i>
                <p>No label assignments found for this player.</p>
              </div>
              <div v-else class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table class="w-full">
                  <thead class="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Team Name</th>
                      <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Assigned Label</th>
                      <th class="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(assignment, index) in assignedLabelsData" :key="assignment.id" class="border-b border-gray-200 hover:bg-gray-50">
                      <td class="px-4 py-3 text-sm text-gray-900">
                        <div class="flex items-center">
                          <i class="fas fa-shield-alt text-blue-500 mr-2"></i>
                          <span> {{ assignment.team?.team_name }}</span>
                        </div>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-700">
                        <div v-if="assignment.player_label_id" class="flex items-center">
                          <span class="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                            {{ assignment.player_label.label }}
                          </span>
                        </div>
                        <div v-else class="flex items-center">
                          <span class="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-medium">
                            No label assigned
                          </span>
                        </div>
                      </td>
                      <td class="px-4 py-3 text-center">
                        <div class="flex items-center justify-center space-x-2 relative">
                          <button 
                            @click="showLabelSelection(assignment.id)"
                            class="px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors"
                          >
                            <i class="fas fa-plus mr-1"></i>
                            Assign Label
                          </button>
                          <!-- <button 
                            v-if="assignment.player_label_id"
                            @click="removeAssignedLabel(assignment.id)"
                            class="px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 transition-colors"
                          >
                            <i class="fas fa-times mr-1"></i>
                            Remove
                          </button> -->
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <!-- Raw JSON data (collapsible for debugging) -->
              <details class="mt-6">
                <summary class="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800">
                  <i class="fas fa-code mr-2"></i>
                  View Raw API Response
                </summary>
                <div class="mt-2 bg-gray-50 p-4 rounded-lg">
                  <pre class="text-xs text-gray-700 bg-white p-3 rounded border overflow-auto max-h-64">{{ JSON.stringify(assignedLabelsData, null, 2) }}</pre>
                </div>
              </details>
            </div>
            
            <!-- No Data State -->
            <div v-else class="flex items-center justify-center py-12">
              <div class="text-center text-gray-500">
                <i class="fas fa-chart-bar text-2xl mb-2"></i>
                <p class="text-sm">No assigned labels data available. Click "Refresh" to fetch data.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Aliases Tab Content -->
      <div v-else-if="activeTab === 'Aliases'" class="space-y-6">
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div class="bg-gray-50 p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Player Aliases</h3>
              <p class="text-sm text-gray-600 mt-1">View all aliases for this player.</p>
            </div>
            <div class="flex gap-2">
              <button 
                @click="fetchAliasesData"
                :disabled="isLoadingAliases"
                class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <i v-if="isLoadingAliases" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-sync-alt"></i>
                {{ isLoadingAliases ? 'Loading...' : 'Refresh' }}
              </button>
            </div>
          </div>
          
          <!-- Content Container -->
          <div class="overflow-x-auto overflow-y-auto flex-1">
            <!-- Loading State -->
            <div v-if="isLoadingAliases" class="flex items-center justify-center py-12">
              <div class="text-center text-slate-500">
                <i class="fas fa-spinner fa-spin text-2xl mb-2"></i>
                <p class="text-sm">Loading aliases data...</p>
              </div>
            </div>
            
            <!-- Error State -->
            <div v-else-if="aliasesError" class="flex items-center justify-center py-12">
              <div class="text-center text-red-500">
                <i class="fas fa-exclamation-triangle text-2xl mb-2"></i>
                <p class="text-sm">{{ aliasesError }}</p>
                <button 
                  @click="fetchAliasesData"
                  class="mt-2 px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                >
                  Retry
                </button>
              </div>
            </div>
            
            <!-- Data Display -->
            <div v-else-if="aliasesData" class="p-6">
              <div v-if="aliasesData.length === 0" class="text-center text-gray-500 py-12">
                <i class="fas fa-info-circle text-2xl mb-2"></i>
                <p>No aliases found for this player.</p>
              </div>
              <div v-else class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table class="w-full">
                  <thead class="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">ID</th>
                      <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Player ID</th>
                      <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Alias Name</th>
                      <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Created At</th>
                      <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Updated At</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(alias, index) in aliasesData" :key="alias.id || index" class="border-b border-gray-200 hover:bg-gray-50">
                      <td class="px-4 py-3 text-sm text-gray-900 font-medium">
                        {{ alias.id || '-' }}
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-700">
                        {{ alias.player_id || '-' }}
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-900">
                        <div class="flex items-center">
                          <i class="fas fa-user-tag text-blue-500 mr-2"></i>
                          <span class="font-medium">{{ alias.player_name || '-' }}</span>
                        </div>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-700">
                        {{ alias.created_at || '-' }}
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-700">
                        {{ alias.updated_at || '-' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <!-- Raw JSON data (collapsible for debugging) -->
              <details class="mt-6">
                <summary class="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800">
                  <i class="fas fa-code mr-2"></i>
                  View Raw API Response
                </summary>
                <div class="mt-2 bg-gray-50 p-4 rounded-lg">
                  <pre class="text-xs text-gray-700 bg-white p-3 rounded border overflow-auto max-h-64">{{ JSON.stringify(aliasesData, null, 2) }}</pre>
                </div>
              </details>
            </div>
            
            <!-- No Data State -->
            <div v-else class="flex items-center justify-center py-12">
              <div class="text-center text-gray-500">
                <i class="fas fa-user-tag text-2xl mb-2"></i>
                <p class="text-sm">No aliases data available. Click "Refresh" to fetch data.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Other Tab Content Placeholders -->
      <div v-else class="text-center py-12">
        <div class="text-gray-500 text-lg">{{ activeTab }} content coming soon...</div>
      </div>
    </div>
    
    <!-- Label Selection Popup Overlay -->
    <div 
      v-if="showLabelPopup"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      @click="handleClickOutside"
    >
      <div 
        class="label-popup bg-white rounded-lg shadow-xl w-96 max-h-[80vh] overflow-hidden"
        @click="handlePopupClick"
      >
        <div class="p-3 border-b border-gray-200">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700">Select Label</span>
            <button 
              @click="closeLabelPopup"
              class="text-gray-400 hover:text-gray-600"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <!-- Search Input -->
          <div class="relative">
            <input
              v-model="labelSearchQuery"
              type="text"
              placeholder="Search labels..."
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <i class="fas fa-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
          </div>
        </div>
        
        <div class="max-h-48 overflow-y-auto">
          <div v-if="isLoadingLabels" class="p-4 text-center">
            <i class="fas fa-spinner fa-spin text-blue-500"></i>
            <p class="text-sm text-gray-500 mt-1">Loading labels...</p>
          </div>
          <div v-else-if="availableLabels.length === 0" class="p-4 text-center text-gray-500">
            <p class="text-sm">No labels available</p>
          </div>
          <div v-else-if="filteredLabels.length === 0" class="p-4 text-center text-gray-500">
            <p class="text-sm">No labels match your search</p>
          </div>
          <div v-else class="py-1">
            <button
              v-for="label in filteredLabels"
              :key="label.id"
              @click="assignLabel(label.id)"
              class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
            >
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <div class="font-medium">{{ label.label }}</div>
                  <div v-if="label.description || label.label_description" class="text-xs text-gray-500 mt-1">
                    {{ label.description || label.label_description }}
                  </div>
                </div>
                <i class="fas fa-chevron-right text-xs text-gray-400 ml-2"></i>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Season Selection Popup -->
    <SeasonSelectionPopup
      :is-visible="showSeasonSelectionPopup"
      :available-seasons="availableSeasons"
      :selected-seasons="selectedSeasons"
      @close="closeSeasonSelection"
      @update-selected-seasons="handleSeasonSelection"
    />
    
    <!-- Season Details Modal -->
    <SeasonDetailsModal
      :season="selectedSeasonForModal"
      :player-id="selectedPlayerIdForModal"
      :show="showSeasonDetailsModal"
      :template-data="selectedTemplateData"
      :context="modalContext"
      @close="closeSeasonDetailsModal"
    />
    
    <!-- League Details Modal -->
    <LeagueDetailsModal
      v-if="selectedLeagueForModal && selectedTeamForModal"
      :league="selectedLeagueForModal"
      :team="selectedTeamForModal"
      :player-id="player.id || player.player_id"
      :time-frame="selectedTimeFrameForModal"
      :show="showLeagueDetailsModal"
      @close="closeLeagueDetailsModal"
    />
    
    <!-- Template Configurator -->
    <TemplateConfigurator
      :player-id="player.id || player.player_id"
      :player-name="player.name || player.player_name"
      :template-label="selectedTemplateLabel"
      :show="showTemplateConfigurator"
      @close="closeTemplateConfigurator"
      @saved="handleTemplateSaved"
    />
  </div>
</template> 